import type { Offer } from "../api/offers";
import type { Order } from "../api/orders";

/** An order already standing on the offer, in the shape the offer screen shows it. */
export type MadeOrder = Pick<Order, "id" | "orderNumber" | "status">;

/**
 * What the offer screen does with the "make an order" action.
 *
 * `again` carries the orders that already came out of this offer, so the screen names them
 * and asks before a second one is made. `unknown` is the honest answer when the check itself
 * failed: the action stays reachable, but it asks first rather than pretending the offer is
 * untouched.
 */
export type ToOrderAction =
  | { kind: "none" }
  | { kind: "checking" }
  | { kind: "blocked"; reason: "noCompany" }
  | { kind: "create" }
  | { kind: "again"; made: MadeOrder[] }
  | { kind: "unknown" };

/**
 * Decides the action from what the screen knows.
 *
 * `offer` is null while the offer is new or still loading — there is nothing to convert yet.
 * `made` is the server's answer (an empty list means "nothing came out of this offer yet"),
 * null means the answer is not in: either still being read (`checking`) or the read failed.
 *
 * An offer without a customer company cannot become an order — the server refuses it with
 * OFFER_WITHOUT_COMPANY — so the screen says so instead of offering a button that comes back
 * refused.
 */
export function toOrderAction(
  offer: Pick<Offer, "companyId"> | null,
  canCreateOrder: boolean,
  made: MadeOrder[] | null,
  checking: boolean,
): ToOrderAction {
  if (!offer || !canCreateOrder) return { kind: "none" };
  if (!offer.companyId) return { kind: "blocked", reason: "noCompany" };
  if (made == null) return checking ? { kind: "checking" } : { kind: "unknown" };
  if (made.length > 0) return { kind: "again", made };
  return { kind: "create" };
}

/** True when the action may be clicked at all. */
export function toOrderClickable(action: ToOrderAction): boolean {
  return action.kind === "create" || action.kind === "again" || action.kind === "unknown";
}

/** The order numbers already standing on the offer, for the question the screen asks. */
export function madeOrderNumbers(made: MadeOrder[]): string {
  return made.map((o) => o.orderNumber).join(", ");
}
